import { FC, Key, memo, useContext, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyOverviewGetEnergyUsageV2Post,
  V2EnergyOverviewGetEnergyUsageV2PostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { ShareDataUnityEnergyCockpitMapContext } from '../../../..';
import styles from './index.module.scss';
import { Carousel, Divider, Progress, Spin } from 'antd';
import { chunkArray, formatName, getDefaultTime } from '@/pages/WgqAllMaps/utils';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { isEmpty } from 'lodash-es';
import { ChartColors, getBarChartOption } from './chart';
import { formatOneDecimalOrDash, formatThousandAndUnit, formatThousandUnit, keepOneOrLine } from '@/utils/utils';
import { TaskScene } from '../../../../const';
import SwitchButton from './SwitchButton';

export type EnergyUsageItem = Exclude<V2EnergyOverviewGetEnergyUsageV2PostResponse['list'], undefined>[number];
const EnergyUsage = () => {
  const { timeResolution, dateValue } = useContext(ShareDataUnityEnergyCockpitMapContext);
  const [selectedId, setSelectedId] = useState<Key>(1);
  const [usageData, setUsageData] = useState<EnergyUsageItem>();
  const theme = useTheme();

  const { data: list, loading } = useRequest(
    async () => {
      const res = await apiV2EnergyOverviewGetEnergyUsageV2Post({
        timeResolution,
        fromTime: getDefaultTime(timeResolution!, dateValue)?.startTime,
        toTime: getDefaultTime(timeResolution!, dateValue)?.endTime,
      });
      return res.list;
    },
    {
      refreshDeps: [timeResolution, dateValue],
      ready: !!timeResolution && !!dateValue,
    }
  );

  useEffect(() => {
    if (list) {
      setUsageData(list?.find(item => item.id === selectedId));
    }
  }, [list, selectedId]);

  const onUnitChange = (value: Key) => {
    setSelectedId(value);
  };

  const percentList = useMemo(() => {
    if (!usageData?.usageDataList || !Number.isFinite(usageData?.total)) {
      return [];
    }

    const values = usageData.usageDataList.map(item => Number(item?.value ?? 0));
    const total = Number(usageData.total);

    if (total <= 0) return [];

    let sum = 0;

    return values.map((val, index) => {
      if (index === values.length - 1) {
        return Number((100 - sum).toFixed(1));
      }

      const percent = Number(((val / total) * 100).toFixed(1));
      sum += percent;
      return percent;
    });
  }, [usageData]);

  const chartBarOption = useMemo(() => getBarChartOption(usageData, timeResolution), [usageData, timeResolution]);

  const ElectricCarouselRender = useMemo(
    () => (
      <div className={styles.usageList}>
        {usageData?.usageDataList?.map((item, index) => {
          const { value, unit } = formatThousandAndUnit(item.value);
          return (
            <div key={item.name} className={styles.usageItem}>
              <div
                style={{
                  color: ChartColors[TaskScene.ELECTRICITY][index],
                }}
              >
                {formatName(item.name)}({usageData?.physicalUnitName}):
              </div>
              <div>{percentList[index] ?? '--'}%</div>
              <div>
                {value}
                <span className={styles.unit}>{unit}</span>
              </div>
            </div>
          );
        })}
      </div>
    ),
    [usageData, percentList]
  );
  const GasCarouselRender = useMemo(() => {
    return (
      <div>
        {!isEmpty(usageData?.mediumDataList) && (
          <Carousel
            autoplay
            autoplaySpeed={2000}
            dotPosition="bottom"
            dots={usageData?.mediumDataList && usageData.mediumDataList.length > 3}
            className={styles.medium}
          >
            {chunkArray(usageData?.mediumDataList, 3).map((group, idx) => (
              <div key={idx} className={styles.mediumList}>
                {group.map((item, index) => (
                  <div key={index} className={styles.mediumItem}>
                    <div className={styles.mediumHeader}>
                      <div>
                        {formatName(item.name)}({usageData?.physicalUnitName ?? '--'})
                      </div>
                      <div className={styles.value}>{formatThousandUnit(item.value)}</div>
                    </div>
                    <div className={styles.periodInfo}>
                      <div className={styles.periodHeader}>
                        生产时段
                        <span className={styles.proportion}>
                          {formatOneDecimalOrDash(item.periodData?.proportion) + '%'}
                        </span>
                      </div>
                      <div className={styles.periodValue} style={{ color: '#16DD8E' }}>
                        {formatThousandAndUnit(item.periodData?.value).value}
                        <span className={styles.unit}> {formatThousandAndUnit(item.periodData?.value).unit}</span>
                      </div>
                    </div>
                    <Divider dashed style={{ margin: 0, borderColor: 'rgba(255,255,255,0.2)' }} />
                    <div className={styles.periodInfo}>
                      <div className={styles.periodHeader}>
                        非生产时段
                        <span className={styles.proportion}>
                          {formatOneDecimalOrDash(item.nonPeriodData?.proportion) + '%'}
                        </span>
                      </div>
                      <div className={styles.periodValue} style={{ color: '#FFCB47' }}>
                        {formatThousandAndUnit(item.nonPeriodData?.value).value}
                        <span className={styles.unit}> {formatThousandAndUnit(item.nonPeriodData?.value).unit}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </Carousel>
        )}
      </div>
    );
  }, [usageData]);

  const WaterCarouselRender = useMemo(() => {
    return (
      <div>
        {!isEmpty(usageData?.mediumDataList) && (
          <Carousel
            autoplay
            autoplaySpeed={2000}
            dotPosition="bottom"
            dots={usageData?.mediumDataList && usageData.mediumDataList.length > 2}
            className={styles.medium}
          >
            {chunkArray(usageData?.mediumDataList, 2).map((group, idx) => (
              <div key={idx} className={styles.mediumList}>
                {group.map((item, index) => (
                  <div key={index} className={styles.mediumItem} style={{ minWidth: 200, maxWidth: 220 }}>
                    <div className={styles.waterMediumHeader}>
                      <div>
                        <div>
                          {formatName(item.name)}({usageData?.physicalUnitName ?? '--'})
                        </div>
                        <div className={styles.value}>{formatThousandUnit(item.value)}</div>
                      </div>
                      <div className={styles.progressInfo}>
                        <Progress
                          type="circle"
                          size={40}
                          percent={100}
                          strokeWidth={8}
                          showInfo={false}
                          trailColor="transparent"
                          strokeColor={{
                            '0%': 'rgba(37,130,183,0.4)',
                            '30%': 'rgba(37,130,183,0.1)',
                            '40%': 'transparent',
                            '60%': 'transparent',
                            '70%': 'rgba(68,120,155,0.1)',
                            '100%': 'rgba(37,130,183,0.4)',
                          }}
                        />
                        <div className={styles.value}>{formatOneDecimalOrDash(item?.proportion) + '%'}</div>
                      </div>
                    </div>
                    <div className={styles.periodInfo}>
                      <div className={styles.periodHeader}>
                        生产时段
                        <span className={styles.proportion}>
                          {formatOneDecimalOrDash(item.periodData?.proportion) + '%'}
                        </span>
                      </div>
                      <div className={styles.periodValue} style={{ color: '#16DD8E' }}>
                        {formatThousandAndUnit(item.periodData?.value).value}
                        <span className={styles.unit}> {formatThousandAndUnit(item.periodData?.value).unit}</span>
                      </div>
                    </div>
                    <Divider dashed style={{ margin: 0, borderColor: 'rgba(255,255,255,0.2)' }} />
                    <div className={styles.periodInfo}>
                      <div className={styles.periodHeader}>
                        非生产时段
                        <span className={styles.proportion}>
                          {formatOneDecimalOrDash(item.nonPeriodData?.proportion) + '%'}
                        </span>
                      </div>
                      <div className={styles.periodValue} style={{ color: '#FFCB47' }}>
                        {formatThousandAndUnit(item.nonPeriodData?.value).value}
                        <span className={styles.unit}> {formatThousandAndUnit(item.nonPeriodData?.value).unit}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </Carousel>
        )}
      </div>
    );
  }, [usageData]);

  return (
    <div className={styles.container}>
      <Spin spinning={loading}>
        <SwitchButton
          className={styles.switchTab}
          defaultValue={selectedId ?? 1}
          onChange={onUnitChange}
          options={list?.map(item => ({ label: item.name!, value: item.id! })) ?? []}
        />
        {selectedId === 1 && usageData && (
          <div className={styles.electricBar}>
            <div className={styles.total}>
              <div>
                {usageData.name}({usageData.physicalUnitName ?? '--'})
              </div>
              <div className={styles.value}>{formatThousandUnit(usageData.total)}</div>
            </div>
            <div className={styles.divider} />
            <div className={styles.periodInfo}>
              <div className={styles.periodHeader}>生产时段({usageData.physicalUnitName ?? '--'})</div>
              <div className={styles.periodValue} style={{ color: '#16DD8E' }}>
                {formatThousandUnit(usageData.periodData?.value)}
              </div>
              <div className={styles.proportion}>{formatOneDecimalOrDash(usageData.periodData?.proportion) + '%'}</div>
            </div>
            <div className={styles.divider} />
            <div className={styles.periodInfo}>
              <div className={styles.periodHeader}>非生产时段({usageData.physicalUnitName ?? '--'})</div>
              <div className={styles.periodValue} style={{ color: '#FFCB47' }}>
                {formatThousandUnit(usageData.nonPeriodData?.value)}
              </div>
              <div className={styles.proportion}>
                {formatOneDecimalOrDash(usageData.nonPeriodData?.proportion) + '%'}
              </div>
            </div>
          </div>
        )}
        {selectedId === 1 && ElectricCarouselRender}
        {selectedId === 2 && WaterCarouselRender}
        {selectedId === 3 && GasCarouselRender}
        <div style={{ flex: 1, minHeight: 220 }}>
          <EchartsWithTheme
            option={chartBarOption}
            empty={{
              isShowEmpty: isEmpty(usageData?.chartDataList),
              emptyNode: <Empty style={{ marginTop: 60, minHeight: 220 }} />,
            }}
            theme={theme}
            style={{ height: '100%', width: '100%', minHeight: 220 }}
            params={{
              dataZoomExit: true,
            }}
          />
        </div>
      </Spin>
    </div>
  );
};

export default memo(EnergyUsage);
